/* eslint-disable no-unused-vars */
import React from 'react';
import PropTypes from 'prop-types';
import Tnc from 'gatsby-core-theme/src/components/molecules/tnc';
import useTranslate from '~hooks/useTranslate/useTranslate';
import PrettyLink from '~atoms/pretty-link';
import OperatorCta from '~atoms/button/operator-cta';
import LazyImage from '~hooks/lazy-image';
import { imagePrettyUrl, getExtraField } from '~helpers/getters';
import { getAltText } from '~helpers/image';
import { getRating } from '~helpers/rating';
import StarIcon from '../../../../images/star.svg';
import ArrowIcon from '../../../../images/icons/arrow';
import CheckIcon from '../../../../images/icons/check';
import SellingPoints from '~atoms/selling-points';
import { TrackingKeys } from '~constants/tracking-api';

import styles from './grid-item.module.scss';

const GridItem = ({
  item,
  oneliner = 'main',
  layout = 'list',
  tracker = 'main',
  pageTemplate,
  itemRefs,
  index,
  className,
  moduleName = TrackingKeys?.TOPLIST,
  pagePath,
  buttonIcon = <ArrowIcon />,
  modulePosition,
  itemPosition,
}) => {
  const ratingJson = {
    5: {
      statusName: useTranslate('exellent', 'Exellent'),
    },
    4: {
      statusName: useTranslate('very_good', 'Very Good'),
    },
    3: {
      statusName: useTranslate('good', 'Good'),
    },
    2: {
      statusName: useTranslate('average', 'Average'),
    },
    1: {
      statusName: useTranslate('not_recommended', 'Not Recommended'),
    },
    0: {
      statusName: useTranslate('not_recommended', 'Not Recommended'),
    },
    NoRating: {
      statusName: useTranslate('no_score_Provided', 'No Score Provided'),
    },
  };
  const addToRefs = (el, itemNum) => {
    if (el && itemRefs) {
      itemRefs.current[itemNum] = el;
    }
  };
  const trackerType = tracker.toLowerCase().replace(' ', '_');
  const image = item?.logo;
  const itemName = item?.name;
  const rating = item?.rating ? getRating(item?.rating, true) : null;
  const ribbon = item?.ribbons && item?.ribbons[0];
  const onelinerMain = item?.bonuses[oneliner]?.one_liner.split('|')[0];
  const onelinerSecond = item?.bonuses[oneliner]?.one_liner.split('|')[1];
  const fullRating = rating && rating % 1 !== 0 ? rating + 0.5 : rating;
  return (
    <div className={styles.container} ref={(el) => addToRefs(el, index)}>
      <div className={styles.topSection}>
        <PrettyLink
          operator={item}
          tracker={trackerType}
          className={styles.logoImageContainer}
          pageTemplate={pageTemplate}
          moduleName={moduleName}
          clickedElement="operator_logo"
          modulePosition={modulePosition}
          itemPosition={itemPosition}
        >
          <LazyImage
            src={imagePrettyUrl(image?.filename, 148, 148)}
            alt={getAltText(image, itemName)}
            width={72}
            height={72}
            className={styles.logoImage}
          />
        </PrettyLink>
        <div className={styles.nameSection}>
          <PrettyLink
            operator={item}
            tracker={trackerType}
            className={styles.operatorName}
            pageTemplate={pageTemplate}
            moduleName={moduleName}
            clickedElement="operator_name"
            modulePosition={modulePosition}
            itemPosition={itemPosition}
          >
            {itemName}
          </PrettyLink>

          <div className={styles.rating}>
            <LazyImage
              src={StarIcon}
              alt={ratingJson[fullRating]?.statusName}
              width={17}
              height={17}
              className={styles.status}
            />
            <div className={styles.ratingInfo}>
              <span className={styles.ratingText}>{ratingJson[fullRating]?.statusName}</span>
            </div>
          </div>
        </div>
        {ribbon && <span className={`${styles.ribbon}`}>{ribbon}</span>}
      </div>
      <div className={styles.sellingPointsC}>
        <SellingPoints sellingPoints={item?.selling_points} icon={<CheckIcon />} />
      </div>
      <div className={styles.bonusContainer}>
        <PrettyLink
          operator={item}
          tracker={tracker}
          pageTemplate={pageTemplate}
          moduleName={moduleName}
          clickedElement="oneliner"
          modulePosition={modulePosition}
          itemPosition={itemPosition}
        >
          {onelinerMain && <p className={styles.oneLinerMain}>{onelinerMain}</p>}
          {onelinerSecond && <p className={styles.oneLinerSecond}>{onelinerSecond}</p>}
        </PrettyLink>

        <OperatorCta
          operator={item}
          moduleName={moduleName}
          pageTemplate={pageTemplate}
          modulePosition={modulePosition}
          itemPosition={itemPosition}
          tracker={tracker}
          icon={buttonIcon}
          gtmClass="toplist-operator-cta-gtm"
          pagePath={pagePath}
        />
      </div>

      <div className={styles.tnc}>
        <Tnc isFixed operator={item} />
      </div>
    </div>
  );
};

GridItem.propTypes = {
  pagePath: PropTypes.string,
  page: PropTypes.shape({
    path: PropTypes.string,
    template: PropTypes.string,
    type: PropTypes.string,
  }),
  className: PropTypes.string,
  itemRefs: PropTypes.shape({
    current: PropTypes.shape({}),
  }),
  index: PropTypes.number,
  oneliner: PropTypes.string,
  layout: PropTypes.string,
  tracker: PropTypes.string,
  moduleName: PropTypes.string,
  item: PropTypes.shape({
    name: PropTypes.string,
    ribbons: PropTypes.arrayOf(PropTypes.string),
    extra_fields: PropTypes.shape({
      toplist_info: PropTypes.string,
    }),
    logo_url: PropTypes.string,
    selling_points: PropTypes.arrayOf(PropTypes.string),
    review_link: PropTypes.string,
    rating: PropTypes.string,
    bonuses: PropTypes.shape({
      main: PropTypes.shape({
        one_liner: PropTypes.string,
      }),
    }),
    logo: PropTypes.shape({
      filename: PropTypes.string,
    }),
    bonus: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
  }).isRequired,
  pageTemplate: PropTypes.string,
  buttonIcon: PropTypes.shape({}),
  modulePosition: PropTypes.number,
  itemPosition: PropTypes.number,
};

export default GridItem;
